//
// Copyright (c) 2011-2026 by Curalate, Inc.
//

import ClientId from '@models/ClientId';
import VibeCommunityId from '@models/VibeCommunityId';

export interface VibeCommunity {
  clientDisplayName: string;
  clientId: ClientId;
  description: string;
  emailUsername: string;
  guidanceCodes: number[];
  hashtagMentions: string[];
  id: VibeCommunityId;
  locale: string;
  logoUrl: string;
  name: string;
  termsConditions: string;
}

export interface VibeCommunitiesSearchResponse {
  count: number;
  results: VibeCommunity[];
}

export interface VibeCommunityTag {
  id: number;
  memberCount: number;
  name: string;
  vibeCommunityId: VibeCommunityId;
}

export interface VibeCommunityTagsResponse {
  results: VibeCommunityTag[];
}

export enum MemberSortOrder {
  ASC = 'asc',
  DESC = 'desc',
}

export interface SearchCommunityMembersRequest {
  communityId: VibeCommunityId;
  limit: number;
  offset: number;
  search?: string;
  sortOrder?: MemberSortOrder;
  status?: CommunityMemberStatus[];
  tagIds?: number[];
}

export enum CommunityMemberStatus {
  ACCEPTED = 'ACCEPTED',
  INVITED = 'INVITED',
  REJECTED = 'REJECTED',
}

export interface CommunityMember {
  email: string;
  firstName: string;
  lastName: string;
  memberId: number;
  status: CommunityMemberStatus;
  tagIds: number[];
}

export interface VibeCommunityMembersSearchResponse {
  count: number;
  filteredCount: number;
  members: CommunityMember[];
  totalInvitesSent: number;
}

export interface ExportCommunityMembersRequest {
  communityId: VibeCommunityId;
  search: string;
  sortOrder?: MemberSortOrder;
  status?: CommunityMemberStatus[];
  tagIds?: number[];
}

export interface CreateCommunityTagRequest {
  communityId: VibeCommunityId;
  name: string;
}

export interface CommunityTagResult {
  id: number;
  name: string;
  vibeCommunityId: VibeCommunityId;
}

export interface CreateCommunityTagResponse {
  result: CommunityTagResult;
}

export interface EditVibeCommunityApiRequest {
  communityId: VibeCommunityId;
  description: string;
  logoUrl: string;
  name: string;
}

export interface EditVibeCommunityRequest extends EditVibeCommunityApiRequest {
  logoFile?: File;
}

export interface UploadVibeCommunityLogoRequest {
  communityId: VibeCommunityId;
  file: File;
}

export interface VibeCommunityLogoResponse {
  logoUrl: string;
}

interface InviteCommunityMembersBaseRequest {
  communityId: VibeCommunityId;
}

export enum InviteLinkType {
  MULTI_USE = 'MULTI_USE',
  SINGLE_USE = 'SINGLE_USE',
}

export interface InviteCommunityMembersRequest
  extends InviteCommunityMembersBaseRequest {
  emails: string[];
  tagIds?: number[];
}

export interface InviteCommunityMembersCsvRequest
  extends InviteCommunityMembersBaseRequest {
  file: File;
  tagIds?: string[]; // FormData requires string values
}

export interface EditVibeCommunityResponse {
  clientDisplayName: string;
  clientId: ClientId;
  description: string;
  emailUsername: string;
  id: VibeCommunityId;
  logoUrl: string;
  name: string;
}

export interface CreateInviteLinkRequest
  extends InviteCommunityMembersBaseRequest {
  expiresAt: number;
  name: string;
  signUpLimit: number;
  tagIds?: number[];
  type: InviteLinkType;
}

export interface VibeCommunityInviteLinkCode {
  deepLinkUrl: string;
  id: number;
  inviteCode: string;
  signUpLimit: number;
  status: InviteLinkStatus;
  usedCount: number;
}

export enum InviteLinkStatus {
  ACTIVE = 'ACTIVE',
  EXPIRED = 'EXPIRED',
  LIMIT_REACHED = 'LIMIT_REACHED',
  WITHDRAWN = 'WITHDRAWN',
}

export interface VibeCommunityInviteLinkTag {
  id: number;
  name: string;
}

interface VibeCommunityInviteLinkBase {
  createdAt: number;
  expiresAt?: number;
  inviteId: number;
  name: string;
  status: InviteLinkStatus;
  tags?: VibeCommunityInviteLinkTag[];
  type: InviteLinkType;
}

export interface CreateInviteLinkResponse extends VibeCommunityInviteLinkBase {
  codes: VibeCommunityInviteLinkCode[];
}

export interface VibeCommunityInviteLink extends VibeCommunityInviteLinkBase {
  codes: VibeCommunityInviteLinkCode[];
  signUpLimit: number;
  totalUsed: number;
}

export type VibeCommunityInviteLinkDetails = VibeCommunityInviteLink;

export interface VibeCommunityInviteLinksResponse {
  count: number;
  results: VibeCommunityInviteLink[];
}

export interface VibeCommunityInviteLinksRequest {
  communityId: VibeCommunityId;
  inviteId?: number;
  limit: number;
  offset: number;
  search?: string;
  sortBy?: string;
  sortOrder?: MemberSortOrder;
  status?: InviteLinkStatus[];
}

export interface EditInviteLinkRequest
  extends InviteCommunityMembersBaseRequest {
  expiresAt?: number;
  inviteId: number;
  name: string;
  status?: InviteLinkStatus;
}

export interface ExportInviteLinksRequest {
  communityId: VibeCommunityId;
  inviteId: number;
}
